// infrastructure/mappers/chargeMasterMapper.js
import { isChargeMinorUnits } from '../../domain/constants/currency';

/**
 * Reads and writes the stored representation of a ChargeMaster sheet.
 *
 * MAPPED FIELD BY FIELD, NEVER SPREAD, on the same terms as its siblings. A spread would carry the
 * Firestore document id, audit stamps and anything a later write happens to add straight into the
 * model, and the model would quietly start meaning whatever storage contained. The cost is that a
 * new field has to be added in both directions or it round-trips to nothing; that cost is the point.
 *
 * EVERY DOCUMENT ID IS DERIVED, INCLUDING VERSION 1 — unlike `fundingSources`, where the series head
 * keeps the auto-id it was created with because other collections already reference it. This
 * collection is net-new with no inbound references, so there is nothing to preserve and every id can
 * be self-describing. That also makes the create path collision-safe on the same terms as an append:
 * two writers creating a clinic's first sheet on one date aim at the same id, and the rules refuse
 * the loser. Deriving the id is only half of that — it holds because the first series key is the
 * CLINIC and not a generated value, so both writers derive from the same thing.
 *
 * A LINE CARRIES ONLY WHAT IT DECIDES. `serviceCodeId` and `chargeMinor`, and nothing resolved from
 * the code — no code string, no descriptor, no unit basis. Those are read through the reference every
 * time they are shown. A snapshot here would be the exact defect the model was built to prevent: a
 * unit basis change mints a new code, so a copied basis could sit beside an unchanged charge and bill
 * fifteen minutes at an hourly rate with nothing throwing.
 */

/**
 * A sheet's document id, derived from its coordinates.
 *
 * DERIVED, NEVER GENERATED, which is what makes concurrent saves safe without a query inside a
 * transaction. Two writers computing the same version number aim at the same id, so the second write
 * lands on a document that already exists — which the rules read as an update and refuse. An auto-id
 * would let both succeed, and two rows for one effective date would then carry `isLatestVersion`,
 * making the as-of query's `LIMIT 1` return whichever Firestore reached first.
 *
 * @param {string} chargeMasterId
 * @param {string} effectiveFrom - 'YYYY-MM-DD'
 * @param {number} version
 * @returns {string}
 */
export function versionDocumentId(chargeMasterId, effectiveFrom, version) {
  return `${chargeMasterId}_${effectiveFrom}_${version}`;
} // versionDocumentId()

/**
 * Stored lines → model lines, dropping any that reference no code.
 *
 * A LINE WITHOUT A `serviceCodeId` IS DROPPED rather than repaired or surfaced. It prices nothing, so
 * there is no code for a reader to resolve it against and no honest way to display it; keeping it
 * would put a row on the sheet that names nothing.
 *
 * `chargeMinor` IS READ AS AN INTEGER OR NULL, with no coercion of a stored string or float. A charge
 * that is not an integer number of minor units is not a charge this system wrote, and treating it as
 * one would let a hand-edited value reach a claim. Null is the ordinary state for a line nobody has
 * charged yet.
 *
 * A NON-LIST `lines` IS READ AS EMPTY rather than thrown on. The rules type the field on write, so
 * this only ever meets one written before they did or edited by hand — and because the page maps
 * every sheet a clinic has, letting one malformed document throw would take the whole charge master
 * down instead of the row that is actually broken.
 */
const toChargeLines = (lines) => {
  return (Array.isArray(lines) ? lines : []).reduce((entries, line) => {
    if (typeof line?.serviceCodeId !== 'string' || line.serviceCodeId.length === 0) {
      return entries;
    }
    return entries.concat([{
      serviceCodeId: line.serviceCodeId,
      chargeMinor: isChargeMinorUnits(line.chargeMinor) ? line.chargeMinor : null,
    }]);
  }, []);
};

/**
 * Firestore document → ChargeMaster.
 *
 * `effectiveFrom` HAS NO DEFAULT. A sheet whose date cannot be read has no position on the timeline,
 * and inventing one would place it somewhere nobody chose. Resolution drops such a row instead, which
 * is the honest outcome.
 *
 * @param {Object} storedChargeMaster - the stored document, including its `id`
 * @returns {import('../../domain/model/ChargeMaster.types').ChargeMaster}
 */
export function toChargeMaster(storedChargeMaster) {
  return {
    versionId: storedChargeMaster?.id ?? null,
    chargeMasterId: storedChargeMaster?.chargeMasterId ?? null,
    clinic: storedChargeMaster?.clinic ?? null,
    effectiveFrom: storedChargeMaster?.effectiveFrom ?? null,
    version: Number(storedChargeMaster?.version) || 1,
    // Absent reads as the live entry: the flag exists to narrow a query, and a document written
    // without it would otherwise vanish from every read that filters on it.
    isLatestVersion: storedChargeMaster?.isLatestVersion !== false,
    currency: storedChargeMaster?.currency ?? null,
    lines: toChargeLines(storedChargeMaster?.lines),
    isDeleted: storedChargeMaster?.isDeleted === true,
  };
} // toChargeMaster()

/** Firestore documents → ChargeMaster[], in the order given. */
export function toChargeMasters(documents) {
  return (documents ?? []).map(toChargeMaster);
} // toChargeMasters()

/**
 * The editable sheet as a document, without the fields the write path owns.
 *
 * TAKES THE RECORD ONLY — no id, no effective date, no version, no clinic, no `isLatestVersion` and
 * no tombstone. Those belong to whichever write is placing this sheet on the timeline, and keeping
 * them out of here means a caller cannot accidentally supply coordinates that disagree with the
 * document id derived from them.
 *
 * @param {{ currency: string, lines: import('../../domain/model/ChargeLine.types').ChargeLine[] }}
 *   record - the whole editable sheet, because the whole sheet versions together
 * @returns {Object}
 */
export function toDocument(record) {
  return {
    currency: record?.currency ?? null,
    lines: toChargeLines(record?.lines),
  };
} // toDocument()
